import { useRef, useState } from "react"
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useSnackbar } from "notistack"
import { interpretarCardapioIA, salvarCardapioCompleto } from "../../../services/cardapio"
import useAppStore from "../../../stores/AppStore"
import { useCardapioContext } from "./CardapioContext"

const useStyles = makeStyles(theme => ({
    fab: {
        position: "fixed",
        bottom: theme.spacing(4),
        right: theme.spacing(4),
        zIndex: 1300,
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        color: "white",
        fontWeight: "bold",
        fontSize: 13,
        boxShadow: "0 4px 20px rgba(124,58,237,0.5)",
        "&:hover": {
            background: "linear-gradient(135deg, #5b21b6 0%, #3730a3 100%)",
            transform: "scale(1.05)",
            boxShadow: "0 6px 24px rgba(124,58,237,0.6)",
        },
        transition: "all 0.2s ease",
    },
    dialogTitle: {
        background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
        color: "white",
        paddingBottom: theme.spacing(2),
    },
    resumoBox: {
        backgroundColor: "#f5f3ff",
        border: "1px solid #ddd6fe",
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    badge: {
        display: "inline-block",
        backgroundColor: "#7c3aed",
        color: "white",
        borderRadius: 12,
        padding: "3px 12px",
        fontSize: 13,
        fontWeight: "bold",
        marginBottom: theme.spacing(1),
    },
    listaAlteracoes: {
        maxHeight: 320,
        overflowY: "auto",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        marginTop: theme.spacing(1),
    },
    itemAlteracao: {
        padding: "8px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        "&:not(:last-child)": {
            borderBottom: "1px solid #f3f4f6",
        },
    },
    breadcrumb: {
        fontSize: 11,
        color: "#6b7280",
        fontStyle: "italic",
    },
    nomeItem: {
        fontSize: 13,
        fontWeight: 600,
        color: "#111827",
    },
    acaoItem: {
        fontSize: 12,
        color: "#4b5563",
    },
    tipoChip: {
        display: "inline-block",
        fontSize: 10,
        fontWeight: 700,
        borderRadius: 4,
        padding: "1px 6px",
        marginRight: 6,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    botaoSalvar: {
        backgroundColor: "#14A44D",
        color: "white",
        "&:hover": { backgroundColor: "#108a3e" },
    },
    botaoDescartar: {
        color: "#dc2626",
        borderColor: "#dc2626",
        "&:hover": { backgroundColor: "#fef2f2" },
    },
    hint: {
        color: theme.palette.text.secondary,
        fontSize: 12,
        marginBottom: theme.spacing(2),
    },
}))

const TIPO_CORES = {
    categoria: { bg: "#ede9fe", color: "#5b21b6", label: "Categoria" },
    produto: { bg: "#e0f2fe", color: "#0369a1", label: "Produto" },
    opcao: { bg: "#fef9c3", color: "#854d0e", label: "Opção" },
}

function descreverMudancas(antes, depois) {
    const acoes = []
    if (antes.ativo !== depois.ativo)
        acoes.push(depois.ativo ? "Ativado" : "Inativado")
    if (antes.nome !== depois.nome)
        acoes.push(`Nome: "${antes.nome}" → "${depois.nome}"`)
    if (antes.valor !== undefined && antes.valor !== depois.valor)
        acoes.push(`Preço: R$ ${Number(antes.valor).toFixed(2)} → R$ ${Number(depois.valor).toFixed(2)}`)
    if (antes.descricao !== undefined && antes.descricao !== depois.descricao)
        acoes.push("Descrição alterada")
    if (antes.ordemExib !== undefined && antes.ordemExib !== depois.ordemExib)
        acoes.push(`Ordem: ${antes.ordemExib} → ${depois.ordemExib}`)
    return acoes
}

function calcularAlteracoes(categoriasAntes, categoriasDepois) {
    if (!categoriasAntes || !categoriasDepois) return []
    const resultado = []

    for (const catDepois of categoriasDepois) {
        const catAntes = categoriasAntes.find(c => c.id === catDepois.id)
        if (!catAntes) continue

        const acoesCat = descreverMudancas(catAntes, catDepois)
        if (acoesCat.length > 0)
            resultado.push({ tipo: "categoria", nome: catDepois.nome, acoes: acoesCat })

        for (const prodDepois of catDepois.produtos || []) {
            const prodAntes = catAntes.produtos?.find(p => p.id === prodDepois.id)
            if (!prodAntes) continue

            const acoesProd = descreverMudancas(prodAntes, prodDepois)
            if (acoesProd.length > 0)
                resultado.push({
                    tipo: "produto",
                    nome: prodDepois.nome,
                    breadcrumb: catDepois.nome,
                    acoes: acoesProd,
                })

            for (const passoDepois of prodDepois.opcoes || []) {
                const passoAntes = prodAntes.opcoes?.find(o => o.id === passoDepois.id)
                if (!passoAntes) continue

                for (const opcaoDepois of passoDepois.opcoes || []) {
                    const opcaoAntes = passoAntes.opcoes?.find(o => o.id === opcaoDepois.id)
                    if (!opcaoAntes) continue

                    const acoesOpcao = descreverMudancas(opcaoAntes, opcaoDepois)
                    if (acoesOpcao.length > 0)
                        resultado.push({
                            tipo: "opcao",
                            nome: opcaoDepois.nome,
                            breadcrumb: `${catDepois.nome} › ${prodDepois.nome} › ${passoDepois.nome}`,
                            acoes: acoesOpcao,
                        })
                }
            }
        }
    }

    return resultado
}

export default function FloatingIAButton() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const perfil = useAppStore(s => s.configuracoes?.user?.perfil)

    const {
        cardapio,
        setCardapio,
        categorias,
        setCategorias,
        setSalvarCardapioCompleto,
        forcarRenderizacao,
        estabelecimento,
    } = useCardapioContext()

    const [open, setOpen] = useState(false)
    const [texto, setTexto] = useState("")
    const [loadingIA, setLoadingIA] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [resultado, setResultado] = useState(null)

    const backupRef = useRef(null)
    const autorizacao = localStorage.getItem("chaveSistema")

    if (perfil !== "CLIENTEFIEL") return null

    const handleEnviar = async () => {
        if (!texto.trim()) return
        setLoadingIA(true)
        try {
            const response = await interpretarCardapioIA(texto.trim(), autorizacao)

            if (response.retornoErro) {
                enqueueSnackbar(response.mensagem, { variant: "error" })
                return
            }
            if (response.erro) {
                enqueueSnackbar(response.erro, { variant: "error" })
                return
            }

            backupRef.current = { cardapio, categorias }

            if (response.cardapio) {
                const alteracoes = calcularAlteracoes(
                    categorias,
                    response.cardapio.categorias
                )
                setCardapio(response.cardapio)
                setCategorias(response.cardapio.categorias || [])
                setSalvarCardapioCompleto(true)
                forcarRenderizacao()
                setResultado({ ...response, alteracoes })
            } else {
                setResultado({ ...response, alteracoes: [] })
            }
        } catch {
            enqueueSnackbar("Erro ao processar a solicitação", { variant: "error" })
        } finally {
            setLoadingIA(false)
        }
    }

    const handleSalvarAgora = async () => {
        setSalvando(true)
        try {
            const response = await salvarCardapioCompleto(
                estabelecimento.id,
                resultado.cardapio,
                autorizacao
            )
            if (response.retornoErro) {
                enqueueSnackbar(response.mensagem, { variant: "error" })
            } else {
                enqueueSnackbar("Cardápio atualizado pela IA com sucesso!", { variant: "success" })
                setSalvarCardapioCompleto(false)
                handleFechar()
            }
        } catch {
            enqueueSnackbar("Erro ao salvar as alterações", { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    const handleDescartar = () => {
        if (backupRef.current) {
            setCardapio(backupRef.current.cardapio)
            setCategorias(backupRef.current.categorias)
            setSalvarCardapioCompleto(false)
            forcarRenderizacao()
        }
        handleFechar()
        enqueueSnackbar("Alterações da IA descartadas", { variant: "info" })
    }

    const handleFechar = () => {
        setOpen(false)
        setTexto("")
        setResultado(null)
        backupRef.current = null
    }

    return (
        <>
            <Tooltip title="Editar cardápio com Inteligência Artificial" placement="left">
                <Fab className={classes.fab} variant="extended" onClick={() => { setTexto(""); setResultado(null); setOpen(true) }}>
                    ✦ IA
                </Fab>
            </Tooltip>

            <Dialog
                open={open}
                onClose={resultado || loadingIA ? undefined : handleFechar}
                maxWidth={resultado ? "md" : "sm"}
                fullWidth
                disableBackdropClick={loadingIA || !!resultado}
            >
                {!resultado ? (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            Editar cardápio com Inteligência Artificial
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <Typography className={classes.hint}>
                                Descreva em linguagem natural o que deseja alterar no cardápio.
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                label="O que deseja fazer?"
                                placeholder='Ex: "Inative todos os itens da categoria Pizzas"'
                                value={texto}
                                onChange={e => setTexto(e.target.value)}
                                disabled={loadingIA}
                                autoFocus
                                onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleEnviar() }}
                            />
                            <Typography style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, textAlign: "right" }}>
                                Ctrl + Enter para enviar
                            </Typography>
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button onClick={handleFechar} disabled={loadingIA}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleEnviar}
                                disabled={loadingIA || !texto.trim()}
                                style={{
                                    background: loadingIA || !texto.trim() ? undefined : "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                                    color: loadingIA || !texto.trim() ? undefined : "white",
                                    minWidth: 130,
                                }}
                                startIcon={loadingIA ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {loadingIA ? "Processando..." : "Enviar ✦"}
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            Resultado da Inteligência Artificial
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 20 }}>
                            <span className={classes.badge}>
                                {resultado.alteracoesRealizadas}{" "}
                                {resultado.alteracoesRealizadas === 1 ? "alteração" : "alterações"}{" "}
                                realizada{resultado.alteracoesRealizadas === 1 ? "" : "s"}
                            </span>

                            <div className={classes.resumoBox}>
                                <Typography variant="body2">{resultado.resumo}</Typography>
                            </div>

                            {resultado.alteracoes?.length > 0 && (
                                <>
                                    <Typography variant="caption" style={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        Itens alterados
                                    </Typography>
                                    <div className={classes.listaAlteracoes}>
                                        {resultado.alteracoes.map((alt, i) => {
                                            const cor = TIPO_CORES[alt.tipo]
                                            return (
                                                <div key={i} className={classes.itemAlteracao}>
                                                    <div>
                                                        <span
                                                            className={classes.tipoChip}
                                                            style={{ backgroundColor: cor.bg, color: cor.color }}
                                                        >
                                                            {cor.label}
                                                        </span>
                                                        <span className={classes.nomeItem}>{alt.nome}</span>
                                                    </div>
                                                    {alt.breadcrumb && (
                                                        <span className={classes.breadcrumb}>{alt.breadcrumb}</span>
                                                    )}
                                                    <span className={classes.acaoItem}>
                                                        {alt.acoes.join(" · ")}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions style={{ padding: "12px 20px" }}>
                            <Button
                                variant="outlined"
                                className={classes.botaoDescartar}
                                onClick={handleDescartar}
                                disabled={salvando}
                            >
                                Descartar
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.botaoSalvar}
                                onClick={handleSalvarAgora}
                                disabled={salvando}
                                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {salvando ? "Salvando..." : "Salvar"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    )
}
